import type { PaginatedPhysicalDock } from 'contracts/PhysicalDocks';
import React, { useCallback, useEffect, useRef } from 'react';
import * as S from './styles';

interface Props {
  docks: PaginatedPhysicalDock[];
  loading: boolean;
  errorMessage: string | null;
  selectedDockIds?: ReadonlySet<number>;
  onToggleDock?: (dockId: number) => void;
  onToggleVisible?: (dockIds: number[], selected: boolean) => void;
  onSelectDock?: (dockId: number) => void;
}

const PhysicalDocksList: React.FC<Props> = ({
  docks,
  loading,
  errorMessage,
  selectedDockIds = new Set<number>(),
  onToggleDock = () => undefined,
  onToggleVisible = () => undefined,
  onSelectDock,
}) => {
  const selectPageRef = useRef<HTMLInputElement>(null);
  const selectedVisibleCount = docks.filter(({ id }) =>
    selectedDockIds.has(id)
  ).length;
  const allVisibleSelected =
    docks.length > 0 && selectedVisibleCount === docks.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  useEffect(() => {
    if (selectPageRef.current) {
      selectPageRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const Item = useCallback(
    ({ dock }: { dock: PaginatedPhysicalDock }): JSX.Element => (
      <S.ListItem>
        <S.SelectionColumn>
          <S.Checkbox
            type="checkbox"
            aria-label={`Selecionar doca ${dock.name}`}
            checked={selectedDockIds.has(dock.id)}
            onChange={() => onToggleDock(dock.id)}
          />
        </S.SelectionColumn>
        <S.Column>{dock.id}</S.Column>
        <S.Column>{dock.name}</S.Column>
        <S.Column>{dock.number}</S.Column>
        <S.Column>
          {dock.building
            ? `${dock.building.area.name} / ${dock.building.name}${
                dock.building.condition ? ` — ${dock.building.condition}` : ''
              }`
            : '—'}
        </S.Column>
        <S.Column>{dock.blockedAt ? 'Bloqueada' : 'Ativa'}</S.Column>
        <S.ActionsColumn>
          <S.ActionButton
            title="Editar Doca Operacional"
            onClick={() => onSelectDock?.(dock.id)}
          >
            <S.EditIcon />
          </S.ActionButton>
        </S.ActionsColumn>
      </S.ListItem>
    ),
    [onSelectDock, onToggleDock, selectedDockIds]
  );

  if (loading) {
    return (
      <S.EmptyListPlaceholder>
        Carregando Docas Físicas...
      </S.EmptyListPlaceholder>
    );
  }

  if (errorMessage) {
    return <S.EmptyListPlaceholder>{errorMessage}</S.EmptyListPlaceholder>;
  }

  if (!docks.length) {
    return (
      <S.EmptyListPlaceholder>
        Este armazém ainda não possui Docas Físicas
      </S.EmptyListPlaceholder>
    );
  }

  return (
    <S.List>
      <S.ListHeader>
        <S.SelectionColumn>
          <S.Checkbox
            ref={selectPageRef}
            type="checkbox"
            aria-label="Selecionar docas desta página"
            checked={allVisibleSelected}
            onChange={(event) =>
              onToggleVisible(
                docks.map(({ id }) => id),
                event.target.checked
              )
            }
          />
        </S.SelectionColumn>
        <div>ID</div>
        <div>NOME</div>
        <div>NÚMERO</div>
        <div>PRÉDIO</div>
        <div>SITUAÇÃO</div>
        <div>EDITAR</div>
      </S.ListHeader>
      {docks.map((dock) => (
        <Item key={dock.id} dock={dock} />
      ))}
    </S.List>
  );
};

export default PhysicalDocksList;
